/**
 * Google Analytics 4 Event Tracking
 *
 * Helper functions for tracking e-commerce events
 */

/**
 * Check if user has consented to analytics
 */
export const hasAnalyticsConsent = () => {
  const consent = localStorage.getItem('ga_cookie_consent');
  return consent === 'accepted';
};

/**
 * Track a page view
 * @param {string} path - Page path
 * @param {string} title - Page title
 */
export const trackPageView = (path, title) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  window.gtag('event', 'page_view', {
    page_path: path,
    page_title: title
  });
};

/**
 * Track product view (view_item)
 * @param {Object} product - Product object
 */
export const trackViewItem = (product) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  window.gtag('event', 'view_item', {
    currency: 'GBP',
    value: product.price / 100, // Convert cents to pounds
    items: [{
      item_id: product.id,
      item_name: product.name,
      price: product.price / 100,
      item_category: product.category?.name || 'Uncategorized',
      quantity: 1
    }]
  });
};

/**
 * Track add to cart (add_to_cart)
 * @param {Object} product - Product object
 * @param {number} quantity - Quantity added
 */
export const trackAddToCart = (product, quantity) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  window.gtag('event', 'add_to_cart', {
    currency: 'GBP',
    value: (product.price * quantity) / 100,
    items: [{
      item_id: product.id,
      item_name: product.name,
      price: product.price / 100,
      item_category: product.category?.name || 'Uncategorized',
      quantity: quantity
    }]
  });
};

/**
 * Track view cart (view_cart)
 * @param {Array} cartItems - Array of cart items
 * @param {number} totalValue - Total cart value in cents
 */
export const trackViewCart = (cartItems, totalValue) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  const items = cartItems.map(item => ({
    item_id: item.productId || item.product?.id,
    item_name: item.product?.name || 'Unknown Product',
    price: item.price / 100,
    item_category: item.product?.category?.name || 'Uncategorized',
    quantity: item.quantity
  }));

  window.gtag('event', 'view_cart', {
    currency: 'GBP',
    value: totalValue / 100,
    items: items
  });
};

/**
 * Track begin checkout (begin_checkout)
 * @param {Array} cartItems - Array of cart items
 * @param {number} totalValue - Total cart value in cents
 */
export const trackBeginCheckout = (cartItems, totalValue) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  const items = cartItems.map(item => ({
    item_id: item.productId || item.product?.id,
    item_name: item.product?.name || 'Unknown Product',
    price: item.price / 100,
    item_category: item.product?.category?.name || 'Uncategorized',
    quantity: item.quantity
  }));

  window.gtag('event', 'begin_checkout', {
    currency: 'GBP',
    value: totalValue / 100,
    items: items
  });
};

/**
 * Track purchase (purchase)
 * @param {Object} order - Order object
 */
export const trackPurchase = (order) => {
  if (!hasAnalyticsConsent() || !window.gtag) return;

  const items = order.items?.map(item => ({
    item_id: item.productId || item.product?.id,
    item_name: item.product?.name || 'Unknown Product',
    price: item.price / 100,
    item_category: item.product?.category?.name || 'Uncategorized',
    quantity: item.quantity
  })) || [];

  window.gtag('event', 'purchase', {
    transaction_id: order.orderNumber || order.id,
    currency: 'GBP',
    value: order.total / 100,
    shipping: order.shippingCost ? order.shippingCost / 100 : 0,
    tax: order.tax ? order.tax / 100 : 0,
    items: items
  });
};
